import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  TextField,
  InputLabel,
  InputAdornment
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { 
  linkPhoneNumber
} from '../utilities/authUtils';
import { PhoneAuthProvider } from 'firebase/auth';
import { useFirebase } from '../contexts/FirebaseContext';
import { getRecaptchaVerifier } from '../utilities/authUtils';

function AccountLinking() {
  const { currentUser, userProfile, updateUserProfile } = useAuth();
  const { auth } = useFirebase();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationId, setVerificationId] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [showVerification, setShowVerification] = useState(false);
  
  const [recaptchaContainerId] = useState(`recaptcha-container-phone-link-${Date.now()}`);
  
  const hasPhoneProvider = currentUser?.providerData?.some(
    provider => provider.providerId === 'phone'
  ) || !!currentUser?.phoneNumber;
  
  const validatePhoneNumber = (phoneNum) => {
    if (!phoneNum) {
      throw new Error('Please enter a phone number');
    }
    
    const cleanedPhoneNumber = phoneNum.replace(/[^0-9+]/g, '');
    const formattedPhoneNumber = cleanedPhoneNumber.startsWith('+') 
      ? cleanedPhoneNumber 
      : `+1${cleanedPhoneNumber}`; // Default to US format if no country code
    
    // Basic validation for E.164 format
    if (!/^\+[1-9]\d{1,14}$/.test(formattedPhoneNumber)) {
      throw new Error('Please enter a valid phone number with country code (e.g., +1 555 555 5555)');
    }
    
    return formattedPhoneNumber;
  };

  const handleStartPhoneVerification = async () => {
    let recaptchaVerifier = null;
    setLoading(true);
    setError(null);
    
    try {
      // Validate and format phone number
      const formattedPhone = validatePhoneNumber(phoneNumber);
      
      // Create reCAPTCHA verifier before sending code
      recaptchaVerifier = await getRecaptchaVerifier(auth, recaptchaContainerId, {
        size: 'invisible',
        callback: () => {
          console.log('reCAPTCHA verified');
        },
        'expired-callback': () => {
          setError('reCAPTCHA expired. Please try again.');
          setLoading(false);
        }
      });
      
      const phoneAuthProvider = new PhoneAuthProvider(auth);
      const verificationId = await phoneAuthProvider.verifyPhoneNumber(
        formattedPhone,
        recaptchaVerifier
      );
      
      setVerificationId(verificationId);
      setShowVerification(true);
      setSuccess("Verification code sent successfully!");
      
    } catch (error) {
      console.error("Error sending verification code:", error);
      setError(error.message);
    } finally {
      if (recaptchaVerifier?.clear) {
        await recaptchaVerifier.clear();
      }
      setLoading(false);
    }
  };

  // Add verification code validation
  const validateVerificationCode = (code) => {
    const cleanCode = code.trim();
    if (!/^\d{6}$/.test(cleanCode)) {
      throw new Error('Please enter a valid 6-digit verification code');
    }
    return cleanCode;
  };

  const handleCompletePhoneVerification = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const cleanCode = validateVerificationCode(verificationCode);
      
      await linkPhoneNumber(currentUser, verificationId, cleanCode);
      
      await currentUser.reload();
      
      const formattedPhone = validatePhoneNumber(phoneNumber);
      
      await updateUserProfile({
        phoneNumber: formattedPhone,
        authProviders: [...(userProfile?.authProviders || []), 'phone']
      });
      
      setSuccess("Phone number linked successfully!");
      setShowVerification(false);
      setPhoneNumber('');
      setVerificationCode('');
      setVerificationId('');
      
    } catch (error) {
      console.error("Error linking phone:", error);
      if (error.code === 'auth/invalid-verification-code') {
        setError('Invalid verification code. Please try again.');
      } else if (error.code === 'auth/code-expired') {
        setError('Verification code has expired. Please request a new code.');
        setShowVerification(false);
      } else {
        setError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="sectionHeader" component="h3" sx={{ pb: 2 }}>
        Connected Accounts
      </Typography>
      
      <Typography variant="body2" color="text.secondary">
        Link multiple authentication methods to your account for easier sign-in and improved security.
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      
      {/* Phone Number Linking */}
      <Box sx={{ my: 3 }}>
        {hasPhoneProvider ? (
          <Alert severity="info" sx={{ my: 2 }}>
            Your account is already linked with a phone number
          </Alert>
        ) : (
          <Box>
            {showVerification ? (
              <Box>
                <InputLabel sx={{ fontWeight: '700' }}>Phone Number</InputLabel>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  disabled={loading}
                  placeholder="+1 (555) 555-5555"
                  variant="outlined"
                  slotProps={{
                    input: {
                      endAdornment: (<InputAdornment position="end">
                        <Button
                          fullWidth
                          variant="contained"
                          onClick={handleStartPhoneVerification}
                          disabled={loading}
                          startIcon={loading ? <CircularProgress size={20} /> : null}
                        >
                          Send Verification Code
                        </Button>
                      </InputAdornment>)
                    }
                  }}
                />
              </Box>
            ) : (
              <Box>
                <InputLabel sx={{ fontWeight: '700' }}>Verification Code</InputLabel>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  disabled={loading}
                  placeholder="555555"
                  variant="outlined"
                  slotProps={{
                    input: {
                      endAdornment: (<InputAdornment position="end">
                        <Button
                          fullWidth
                          variant="contained"
                          onClick={handleCompletePhoneVerification}
                          disabled={loading}
                          startIcon={loading ? <CircularProgress size={20} /> : null}
                        >
                          Verify Code
                        </Button>
                      </InputAdornment>)
                    }
                  }}
                />
              </Box>
            )}
          </Box>
        )}
        
        {/* reCAPTCHA container */}
        <div id={recaptchaContainerId} />
      </Box>
    </Box>
  );
}

export default AccountLinking; 